<template>
  <component
    :is="isCardLink ? 'router-link' : 'section'"
    :to="isCardLink ? linkTo : null"
    class="app-panel"
    :class="{ 'app-panel--link': isCardLink, 'app-panel--nested': nested }"
  >
    <div class="app-panel__strip" :class="{ 'app-panel__strip--action': hasEnd() }">
      <span v-if="icon" class="app-icon-tile" aria-hidden="true"><app-icon :name="icon" :size="14" /></span>
      <h2 class="app-panel__title">{{ title }}</h2>
      <span class="app-panel__subtitle"><slot name="subtitle">{{ subtitle }}</slot></span>
      <div v-if="hasSlot('actions')" class="app-panel__end"><slot name="actions" /></div>
      <router-link
        v-else-if="showLink"
        :to="linkTo"
        class="app-link"
        :data-flush="touch ? '' : null"
      >{{ linkText }}<app-icon name="chevR" :size="14" /></router-link>
      <span v-if="isCardLink" class="app-panel__chev" aria-hidden="true"><app-icon name="chevR" :size="16" /></span>
    </div>
    <div
      class="app-panel__body"
      :class="{ 'app-panel__body--flush': flush, 'app-panel__body--state': !!state }"
      :aria-busy="state === 'loading' ? 'true' : null"
    >
      <slot />
      <div v-if="showSkeleton" class="app-panel__skeleton">
        <app-skeleton :kind="skeleton" :rows="skeletonRows" />
        <span class="app-sr-only" role="status">Wird geladen …</span>
      </div>
      <app-state-overlay
        v-else-if="state"
        :state="state"
        :offset-top="0"
        :title="stateTitle"
        :text="stateText"
        :icon="stateIcon"
        @retry="$emit('retry')"
      />
    </div>
    <div v-if="hasSlot('foot')" class="app-panel__foot"><slot name="foot" /></div>
  </component>
</template>

<script>
// Karte mit grauem Kopfstreifen (Spezifikation 2.10, gen_r3.py:460–488).
// Link „Verkäufe ›“ rechts im Streifen über linkTo + linkText; statt des Links Slot actions (z. B. Icon-Knöpfe).
// cardLink: die ganze Karte ist ein router-link (linkTo) mit › im Streifen (Handy; innen keine weiteren Links).
// state ('loading' | 'empty' | 'error' | 'forbidden') legt AppStateOverlay über den Körper; Ereignis retry.
// flush: Körper ohne Polster (z. B. Tabellen, Listen mit eigenem Polster).
// nested: verschachtelte Karte (Übersicht, Kundenwunsch 12.10.2026) – getönter, runder Rahmen mit dem Kopf darauf,
// der Körper als weiße innere Karte.
// Slot foot: Fußzeile unter dem Körper (nested: auf dem getönten Rahmen wie bei den Kennzahl-Kacheln; links 500
// --ink-2, rechts --ink-3), z. B. Legende oder „weitere …“.
// skeleton ('bars' | 'donut' | 'rows' | 'block'): beim Laden statt Kreisel ein animiertes Skelett in der Gestalt des
// Inhalts (AppSkeleton), über dem Körper wie der Ladezustand, damit sich die Höhe nicht ändert; skeletonRows für 'rows'.
import AppIcon from './AppIcon.vue';
import AppSkeleton from './AppSkeleton.vue';
import AppStateOverlay from './AppStateOverlay.vue';

export default {
  name: 'AppPanel',
  components: { AppIcon, AppSkeleton, AppStateOverlay },
  props: {
    nested: { type: Boolean, default: false },
    icon: { type: String, default: null },
    title: { type: String, default: '' },
    subtitle: { type: String, default: null },
    linkTo: { type: [String, Object], default: null },
    linkText: { type: String, default: null },
    cardLink: { type: Boolean, default: false },
    state: { type: String, default: null },
    stateTitle: { type: String, default: null },
    stateText: { type: String, default: null },
    stateIcon: { type: String, default: null },
    flush: { type: Boolean, default: false },
    skeleton: { type: String, default: null },
    skeletonRows: { type: Number, default: 3 }
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    isCardLink() {
      return this.cardLink && !!this.linkTo;
    },
    showLink() {
      return !this.isCardLink && !!this.linkTo && !!this.linkText;
    },
    showSkeleton() {
      return this.state === 'loading' && !!this.skeleton;
    }
  },
  methods: {
    hasSlot(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name]);
    },
    hasEnd() {
      return this.hasSlot('actions') || this.showLink;
    }
  }
};
</script>
